import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, User, Database, Activity, AlertTriangle, MapPin, Monitor, CheckCircle, XCircle, Clock, Shield, Search, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import AuthService from '../../Entities/AuthService';
import { translateOperation, translateAction, translateTable, translateResourceType, translateField, translateEventType, formatChangeValue } from './auditFormatters';

/**
 * Botão de investigação de IP sob demanda (GET /api/geolocation/ip/:ip).
 * Só renderiza algo para super_admin — a rota é restrita a esse papel.
 * Separado da "Localização" já exibida no InfoItem acima, que vem da
 * captura automática feita no momento do login/ação (ver docs/FLUXOS.md).
 */
const IPInvestigateButton = ({ ip }) => {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  if (!AuthService.isSuperAdmin() || !ip) return null;

  const investigate = async () => {
    setStatus('loading');
    try {
      const response = await axios.get(`/api/geolocation/ip/${encodeURIComponent(ip)}`);
      if (response.data.success && response.data.data) {
        setResult(response.data.data);
        setStatus('done');
      } else {
        setErrorMessage(response.data.error || 'Não foi possível localizar este IP');
        setStatus('error');
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Erro ao consultar geolocalização');
      setStatus('error');
    }
  };

  if (status === 'idle') {
    return (
      <button
        type="button"
        onClick={investigate}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline"
      >
        <Search className="w-3 h-3" />
        Investigar IP
      </button>
    );
  }

  if (status === 'loading') {
    return (
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
        <Loader2 className="w-3 h-3 animate-spin" />
        Consultando geolocalização...
      </p>
    );
  }

  if (status === 'error') {
    return <p className="mt-2 text-xs text-red-600">{errorMessage}</p>;
  }

  const parts = [result.city, result.region, result.country].filter(Boolean);
  return (
    <div className="mt-2 text-xs bg-purple-50 border border-purple-200 rounded-lg p-2">
      <p className="font-medium text-purple-900">{parts.length > 0 ? parts.join(', ') : 'Localização não identificada'}</p>
      {result.isp && <p className="text-purple-700 mt-0.5">Provedor: {result.isp}</p>}
    </div>
  );
};

const AuditDetailsModal = ({ item, type, onClose }) => {
  // Trava a rolagem da página de fundo enquanto o modal está aberto — sem isso,
  // em telas de toque, rolar o dedo sobre o modal também rola a página escurecida
  // atrás dele (mesmo comportamento já usado no ConfirmationDialog).
  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [item]);

  if (!item) return null;

  const formatDate = (date) => {
    if (!date) return 'Não informado';
    return format(new Date(date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", { locale: ptBR });
  };

  const InfoItem = ({ label, value, icon: Icon, highlight, children }) => (
    <div className={`p-3 sm:p-4 rounded-lg border transition-all hover:shadow-md ${
      highlight === 'blue' ? 'bg-blue-50 border-blue-200' :
      highlight === 'green' ? 'bg-green-50 border-green-200' :
      highlight === 'red' ? 'bg-red-50 border-red-200' :
      highlight === 'amber' ? 'bg-amber-50 border-amber-200' :
      highlight === 'purple' ? 'bg-purple-50 border-purple-200' :
      'bg-slate-50 border-slate-200'
    }`}>
      <div className="flex items-start gap-2">
        {Icon && <Icon className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 ${
          highlight === 'blue' ? 'text-blue-600' :
          highlight === 'green' ? 'text-green-600' :
          highlight === 'red' ? 'text-red-600' :
          highlight === 'amber' ? 'text-amber-600' :
          highlight === 'purple' ? 'text-purple-600' :
          'text-slate-600'
        }`} />}
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-slate-600 mb-1">{label}</p>
          <p className="text-sm sm:text-base text-slate-900 break-words">{value || 'Não informado'}</p>
          {children}
        </div>
      </div>
    </div>
  );

  const renderLogDetails = () => {
    if (item.type === 'login') {
      return (
        <>
          {/* Status do Login */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 sm:p-6 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              {item.success ? (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                </div>
              )}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  {item.success ? 'Login Bem-Sucedido' : 'Tentativa de Login Falhou'}
                </h3>
                <p className="text-sm sm:text-base text-slate-600">{item.email}</p>
              </div>
            </div>
            {!item.success && item.failure_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-red-800">
                  <strong>Motivo:</strong> {item.failure_reason}
                </p>
              </div>
            )}
          </div>

          {/* Informações do Usuário */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" />
              Informações do Usuário
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoItem label="Nome" value={item.user?.name || 'Não identificado'} icon={User} />
              <InfoItem label="Email" value={item.email} icon={User} />
            </div>
          </div>

          {/* Informações de Localização */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-600" />
              Localização e Dispositivo
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoItem 
                label="Endereço IP"
                value={item.ip_address}
                icon={Monitor}
                highlight="purple"
              >
                <IPInvestigateButton ip={item.ip_address} />
              </InfoItem>
              <InfoItem 
                label="Localização" 
                value={(() => {
                  const metadata = item.new_values || {};
                  const parts = [];
                  if (metadata.city) parts.push(metadata.city);
                  if (metadata.region) parts.push(metadata.region);
                  if (metadata.country) parts.push(metadata.country);
                  return parts.length > 0 ? parts.join(', ') : 'Não identificada';
                })()} 
                icon={MapPin}
                highlight="purple"
              />
              <InfoItem 
                label="Navegador" 
                value={item.new_values?.browser || 'Não identificado'} 
                icon={Monitor}
              />
              <InfoItem 
                label="Sistema Operacional" 
                value={item.new_values?.os || 'Não identificado'} 
                icon={Monitor}
              />
              <InfoItem 
                label="Dispositivo" 
                value={item.new_values?.device_type || 'Não identificado'} 
                icon={Monitor}
              />
              <InfoItem 
                label="User Agent" 
                value={item.user_agent} 
                icon={Monitor}
              />
            </div>
          </div>
        </>
      );
    } else {
      // Log de ação
      return (
        <>
          {/* Informações da Ação */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 sm:p-6 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  {item.action ? translateAction(item.action) : 'Ação do Sistema'}
                </h3>
                <p className="text-sm sm:text-base text-slate-600">{item.description || 'Sem descrição'}</p>
              </div>
            </div>
          </div>

          {/* Detalhes da Ação */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" />
              Detalhes da Ação
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoItem label="Ação" value={item.action ? translateAction(item.action) : null} icon={Activity} highlight="blue" />
              <InfoItem label="Tipo de Recurso" value={item.resource_type ? translateResourceType(item.resource_type) : null} icon={Database} highlight="blue" />
              <InfoItem label="ID do Recurso" value={item.resource_id} icon={Database} />
              <InfoItem label="Usuário" value={item.user?.name || 'Sistema'} icon={User} />
            </div>
          </div>

          {/* Informações Técnicas */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Monitor className="w-4 h-4 text-purple-600" />
              Informações Técnicas
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoItem label="Endereço IP" value={item.ip_address} icon={Monitor} highlight="purple">
                <IPInvestigateButton ip={item.ip_address} />
              </InfoItem>
              <InfoItem label="User Agent" value={item.user_agent} icon={Monitor} highlight="purple" />
            </div>
          </div>

          {/* Metadados Adicionais */}
          {item.metadata && Object.keys(item.metadata).length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Database className="w-4 h-4 text-amber-600" />
                Metadados Adicionais
              </h4>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <pre className="text-xs sm:text-sm text-slate-900 whitespace-pre-wrap break-words">
                  {JSON.stringify(item.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </>
      );
    }
  };

  const renderDataChangeDetails = () => (
    <>
      {/* Informações da Alteração */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 sm:p-6 rounded-lg border border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center backdrop-blur-sm ${
            item.operation === 'INSERT' ? 'bg-green-100' :
            item.operation === 'UPDATE' ? 'bg-blue-100' :
            'bg-red-100'
          }`}>
            <Database className={`w-6 h-6 sm:w-8 sm:h-8 ${
              item.operation === 'INSERT' ? 'text-green-600' :
              item.operation === 'UPDATE' ? 'text-blue-600' :
              'text-red-600'
            }`} />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900">
              {item.operation === 'INSERT' ? 'Registro Criado' :
               item.operation === 'UPDATE' ? 'Registro Atualizado' :
               'Registro Deletado'}
            </h3>
            <p className="text-sm sm:text-base text-slate-600">Tabela: {item.table_name ? translateTable(item.table_name) : 'Não informado'}</p>
          </div>
        </div>
      </div>

      {/* Informações Básicas */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Database className="w-4 h-4 text-blue-600" />
          Informações da Alteração
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoItem label="Tabela" value={item.table_name ? translateTable(item.table_name) : null} icon={Database} highlight="blue" />
          <InfoItem label="ID do Registro" value={item.record_id} icon={Database} highlight="blue" />
          <InfoItem label="Operação" value={item.operation ? translateOperation(item.operation) : null} icon={Activity} />
          <InfoItem label="Campo Alterado" value={item.field_name ? translateField(item.field_name) : null} icon={Database} />
          <InfoItem label="Usuário" value={item.user?.name || 'Sistema'} icon={User} />
        </div>
      </div>

      {/* Valores Anterior e Novo */}
      {(item.old_value || item.new_value) && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-600" />
            Comparação de Valores
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-red-700 mb-2">VALOR ANTERIOR</p>
              <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                {formatChangeValue(item.field_name, item.old_value, 'Nenhum valor anterior')}
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-green-700 mb-2">NOVO VALOR</p>
              <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                {formatChangeValue(item.field_name, item.new_value, 'Nenhum valor novo')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderSecurityEventDetails = () => (
    <>
      {/* Status do Evento */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 sm:p-6 rounded-lg border border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center backdrop-blur-sm ${
            item.severity === 'critical' ? 'bg-red-100' :
            item.severity === 'high' ? 'bg-orange-100' :
            item.severity === 'medium' ? 'bg-yellow-100' :
            'bg-blue-100'
          }`}>
            <AlertTriangle className={`w-6 h-6 sm:w-8 sm:h-8 ${
              item.severity === 'critical' ? 'text-red-600' :
              item.severity === 'high' ? 'text-orange-600' :
              item.severity === 'medium' ? 'text-yellow-600' :
              'text-blue-600'
            }`} />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900">{item.event_type ? translateEventType(item.event_type) : 'Evento de Segurança'}</h3>
            <p className="text-sm sm:text-base text-slate-600">{item.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            item.severity === 'critical' ? 'bg-red-100 text-red-700' :
            item.severity === 'high' ? 'bg-orange-100 text-orange-700' :
            item.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            Severidade: {item.severity === 'critical' ? 'Crítica' :
                         item.severity === 'high' ? 'Alta' :
                         item.severity === 'medium' ? 'Média' : 'Baixa'}
          </span>
          {item.resolved ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" />
              Resolvido
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              <Clock className="w-3 h-3" />
              Pendente
            </span>
          )}
        </div>
      </div>

      {/* Informações do Evento */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-600" />
          Detalhes do Evento de Segurança
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoItem label="Tipo de Evento" value={item.event_type ? translateEventType(item.event_type) : null} icon={AlertTriangle} highlight="red" />
          <InfoItem label="Usuário" value={item.user?.name || 'Não identificado'} icon={User} />
          <InfoItem label="Endereço IP" value={item.ip_address} icon={Monitor} highlight="purple">
            <IPInvestigateButton ip={item.ip_address} />
          </InfoItem>
          <InfoItem label="User Agent" value={item.user_agent} icon={Monitor} highlight="purple" />
        </div>
      </div>

      {/* Descrição Completa */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-600" />
          Descrição Detalhada
        </h4>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
            {item.description}
          </p>
        </div>
      </div>

      {/* Resolução */}
      {item.resolved && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Informações de Resolução
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <InfoItem 
              label="Resolvido por" 
              value={item.resolved_by_user?.name || 'Não informado'} 
              icon={User}
              highlight="green"
            />
            <InfoItem 
              label="Data de Resolução" 
              value={formatDate(item.resolved_at)} 
              icon={Calendar}
              highlight="green"
            />
            {item.resolution_notes && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-green-700 mb-2">NOTAS DE RESOLUÇÃO</p>
                <p className="text-sm text-slate-900 whitespace-pre-wrap break-words">
                  {item.resolution_notes}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadados */}
      {item.metadata && Object.keys(item.metadata).length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Database className="w-4 h-4 text-purple-600" />
            Metadados do Evento
          </h4>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <pre className="text-xs sm:text-sm text-slate-900 whitespace-pre-wrap break-words">
              {JSON.stringify(item.metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </>
  );

  // Portal: um overlay fixed dentro do fluxo normal da página herda margin de
  // containers pai (ex.: space-y-6), deslocando o fundo escurecido e deixando
  // uma faixa sem cobrir no topo. Portal escapa desse fluxo por completo.
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-lg flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                {type === 'logs' ? <Activity className="w-5 h-5 sm:w-6 sm:h-6" /> :
                 type === 'changes' ? <Database className="w-5 h-5 sm:w-6 sm:h-6" /> :
                 <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />}
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Detalhes do Registro</h2>
                <p className="text-xs sm:text-sm text-blue-100">
                  {type === 'logs' ? 'Log de Auditoria' :
                   type === 'changes' ? 'Alteração de Dados' :
                   'Evento de Segurança'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Data/Hora */}
            <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                <div>
                  <p className="text-xs font-medium text-slate-600">Data e Hora</p>
                  <p className="text-sm sm:text-base font-semibold text-slate-900">
                    {formatDate(item.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Renderizar conteúdo específico por tipo */}
            {type === 'logs' && renderLogDetails()}
            {type === 'changes' && renderDataChangeDetails()}
            {type === 'security' && renderSecurityEventDetails()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default AuditDetailsModal;
